// ============================================================
//  EmailAnalysisResult — shared phishing-analysis result view
// ============================================================
//
// Renders the full tabbed analysis result (verdict banner, scorecard, AI
// outcome, summary, anomalies, and the Header / Sender Controls / Attachments /
// Email Body / Social Engineering / QR / URLs & Advanced tabs) for a single
// analysis `result` object.
//
// It is driven entirely by props so it can be reused anywhere a `result` is
// available — the upload-based Phishing Analysis page and the Defender
// "User Submissions" page both render it.
//
// Props:
//   result      analysis result object (same shape returned by /api/phishing-analysis)
//   file        original email File/Blob — needed for on-demand Advanced Analysis
//   aiEnabled   whether to show the AI verdict (vs "AI Disabled")
//   onClear     called when the user clicks the clear (X) button
//   endpoint    advanced-analysis endpoint (default /api/phishing-analysis/advanced)
//   exportEndpoint  PDF export endpoint (default /api/phishing-analysis/export-pdf)

import React, { useState } from "react";
import {
  FaArrowDown,
  FaCopy,
  FaExternalLinkAlt,
  FaEllipsisV,
  FaEnvelope,
  FaShieldAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaExclamationCircle,
  FaFileAlt,
  FaLink,
  FaList,
  FaPaperclip,
  FaQrcode,
  FaTimes,
  FaUserSecret,
  FaUserShield,
} from "react-icons/fa";
import "./PhishingAnalysis.css";
import {
  useAdvancedAnalysis,
  AdvancedAnalysisButton,
  AdvancedAnalysisCard,
  AdvancedAnalysisModal,
  foldSandboxVerdict,
} from "./AdvancedAnalysis";
import PhishyAssistant from "./PhishyAssistant";
import CustomRuleFindings from "./CustomRuleFindings";
import HomoglyphFindings from "./HomoglyphFindings";
import IocInfoPopover from "./IocInfoPopover";
import "./IocInfoPopover.css";
import { buildReceivedFlow, hopPillClass } from "../../utils/receivedFlow";

/* ---------------------- Verdict Styling ---------------------- */

const mapVerdictToClass = (verdict = "") => {
  const v = String(verdict || "").toLowerCase();
  if (v === "whitelisted" || v.includes("whitelist")) return "pill-ok";
  if (v.includes("needs review")) return "pill-review";
  if (v.includes("potentially malicious") || v === "potentially malicious") return "pill-warn";
  if (v === "malicious" || v === "high" || v.includes("malicious")) return "pill-danger";
  if (v.includes("phish") || v.includes("phishing")) return "pill-danger";
  if (v.includes("suspicious") || v === "suspicious" || v === "medium" || v === "reported")
    return "pill-warn";
  if (v.includes("spam")) return "pill-warn";
  if (v === "clean" || v === "low" || v === "safe" || v.includes("clean") || v.includes("safe"))
    return "pill-ok";
  return "pill-neutral";
};

const formatVerdict = (verdict = "") => {
  const raw = String(verdict || "");
  if (!raw) return "";
  if (raw.toLowerCase() === "suspicious") {
    return raw[0] === raw[0]?.toUpperCase() ? "Suspicious" : "suspicious";
  }
  return raw;
};

// The server returns the AI Analysis Outcome as one combined string with a few
// inline labels ("AI assessment:", "Recommended action:", "Confidence:",
// "Verdict reconciliation:", "Recommended additional checks:"). Keep it as a
// single paragraph but render those labels in bold so they stand out.
// Order matters: longer labels first so "Recommended additional checks" wins
// over "Recommended action" at the same position.
const AI_OUTCOME_LABELS = [
  "AI assessment",
  "Recommended additional checks",
  "Recommended action",
  "Confidence",
  "Verdict reconciliation",
  "Analyst review advised",
];
const renderAiOutcome = (text) => {
  const s = String(text || "");
  if (!s) return null;
  const re = new RegExp(`(${AI_OUTCOME_LABELS.join("|")})\\s*:`, "gi");
  const parts = [];
  let lastIndex = 0;
  let key = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > lastIndex) parts.push(s.slice(lastIndex, m.index));
    parts.push(<strong key={key++}>{m[0]}</strong>);
    lastIndex = re.lastIndex;
  }
  if (lastIndex < s.length) parts.push(s.slice(lastIndex));
  return parts;
};

const mapVerdictToLabel = (verdict = "") => {
  if (!verdict) return "unknown";
  return formatVerdict(verdict);
};

// Shown in place of a provider verdict when that OSINT API hit its rate limit,
// so an analyst knows the data is missing (throttled) rather than "clean".
const RateTag = () => (
  <span
    className="intel-ratelimited"
    title="This provider's API rate limit was hit — reputation unavailable. Re-run shortly."
  >
    <FaExclamationTriangle aria-hidden="true" /> Rate limited
  </span>
);

/* ---------------------- Social Engineering Helpers ---------------------- */

const humanizeCat = (k = "") => {
  const map = {
    urgency: "Urgency / Pressure",
    payment: "Payments / Money",
    credential: "Credentials / Login",
    fear: "Fear / Threat",
  };
  return map[k] || k;
};

const buildSocialEngineeringSummary = (socialIntent) => {
  if (!socialIntent) return "Social engineering checks were not executed.";

  const level = String(socialIntent.level || "low").toLowerCase();
  const indicators = socialIntent.indicators || {};

  const categoriesWithHits = Object.entries(indicators)
    .map(([k, hits]) => ({ k, hits: Array.isArray(hits) ? hits : [] }))
    .filter((x) => x.hits.length > 0);

  if (categoriesWithHits.length === 0) {
    return "No obvious social engineering language was detected in the subject or body.";
  }

  const catNames = categoriesWithHits.map((x) => humanizeCat(x.k));
  const topCats =
    catNames.slice(0, 3).join(", ") + (catNames.length > 3 ? " and others" : "");

  if (level === "high") {
    return `The email contains strong social engineering indicators, primarily around ${topCats}.`;
  }
  if (level === "medium") {
    return `The email contains potential social engineering cues, mainly around ${topCats}.`;
  }
  return `Low-intensity persuasion cues were detected, mainly around ${topCats}.`;
};

const socialLevelToPill = (level = "") => {
  const l = String(level || "").toLowerCase();
  if (l === "high") return "pill-danger";
  if (l === "medium") return "pill-warn";
  return "pill-ok";
};

/* ---------------------- QR Helpers ---------------------- */

const parseQrPayload = (raw = "") => {
  const text = String(raw || "").trim();
  if (!text) return { type: "unknown", value: "" };

  if (/^https?:\/\/\S+/i.test(text)) return { type: "url", value: text };

  if (/^mailto:/i.test(text)) {
    const email = text.replace(/^mailto:/i, "").split("?")[0].trim();
    return { type: "email", value: email || text };
  }

  if (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(text)) {
    return { type: "email", value: text.toLowerCase() };
  }

  if (/^tel:/i.test(text)) return { type: "phone", value: text.replace(/^tel:/i, "").trim() };

  if (/^upi:\/\//i.test(text)) return { type: "upi", value: text };
  if (/@upi\b/i.test(text) && !text.includes(" ")) return { type: "upi-id", value: text };

  return { type: "text", value: text };
};

const renderQrValue = (parsed) => {
  if (!parsed?.value) return <span className="muted">N/A</span>;

  if (parsed.type === "url") {
    return (
      <a href={parsed.value} target="_blank" rel="noreferrer" className="intel-link">
        {parsed.value}
      </a>
    );
  }

  if (parsed.type === "email") {
    return (
      <a href={`mailto:${parsed.value}`} className="intel-link">
        {parsed.value}
      </a>
    );
  }

  if (parsed.type === "phone") {
    return (
      <a href={`tel:${parsed.value}`} className="intel-link">
        {parsed.value}
      </a>
    );
  }

  return <span className="mono mono-wrap">{parsed.value}</span>;
};

/* ============================================================
   COMPONENT
   ============================================================ */

export default function EmailAnalysisResult({
  result,
  file = null,
  aiEnabled = false,
  onClear,
  endpoint = "/api/phishing-analysis/advanced",
  exportEndpoint = "/api/phishing-analysis/export-pdf",
}) {
  const [showRawHeader, setShowRawHeader] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("header");
  const [actionError, setActionError] = useState("");

  const adv = useAdvancedAnalysis({ file, result, endpoint });

  if (!result) return null;

  /* ------------------ Copy Raw Headers ------------------ */
  const handleCopyHeaders = async () => {
    const text = result?.headers?.raw;
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
    setTimeout(() => setCopyStatus(""), 2000);
  };

  /* ------------------ Derived Values ------------------ */
  const headerSec = result?.headerSecurity || {};
  const replyCheck = headerSec?.replyToCheck || null;

  const replyRiskClass =
    replyCheck?.risk === "high"
      ? "pill-danger"
      : replyCheck?.risk === "medium"
      ? "pill-warn"
      : "pill-ok";

  const headerRiskClass =
    headerSec?.riskLevel === "high"
      ? "pill-danger"
      : headerSec?.riskLevel === "medium"
      ? "pill-warn"
      : "pill-ok";

  const fromDomainForFlow = headerSec?.fromDomain || null;
  const receivedFlow = buildReceivedFlow(
    result?.headers?.received || [],
    fromDomainForFlow,
    result?.trustedDomains || [],
    result?.blacklistedDomains || []
  );

  const socialIntent = result?.socialIntent || null;
  const socialLevel = socialIntent?.level || "low";
  const socialScore = typeof socialIntent?.score === "number" ? socialIntent.score : 0;
  const socialIndicators = socialIntent?.indicators || {};
  const socialSummaryText = buildSocialEngineeringSummary(socialIntent);
  const socialPillClass = socialLevelToPill(socialLevel);

  const qrFromAttachmentsForSummary = (result?.attachments || [])
    .filter((a) => a?.qr?.data)
    .map((a) => ({
      source: "attachment",
      name: a?.name || "-",
      mime: a?.mime || "-",
      qrText: a?.qr?.data || "",
    }));

  const qrFromBodyForSummary = (result?.qrFindings || []).map((q) => ({
    source: q?.type === "cid" ? "inline (cid)" : "inline (data-uri)",
    name: q?.fileName || q?.cid || "-",
    mime: q?.mime || "-",
    qrText: q?.qrText || "",
  }));

  const allQrForSummary = [...qrFromAttachmentsForSummary, ...qrFromBodyForSummary];
  const qrCount = allQrForSummary.length;

  const qrPreview = (() => {
    if (qrCount === 0) return null;
    const first = String(allQrForSummary[0]?.qrText || "").trim();
    if (!first) return "decoded value present";
    return first.length > 90 ? `${first.slice(0, 90)}…` : first;
  })();

  const verdictText =
    result?.aiRawVerdict || result?.aiVerdict || result?.verdict || "No verdict available";
  const aiEnabledForResult = aiEnabled;

  const verdictClass = aiEnabledForResult ? mapVerdictToClass(verdictText) : "pill-neutral";

  const vtDomainStats = headerSec?.vtDomain || null;

  let vtDomainScore = null;
  let vtDomainScoreClass = "summary-entity-vt summary-entity-vt-neutral";

  if (
    vtDomainStats &&
    typeof vtDomainStats.positives === "number" &&
    typeof vtDomainStats.total === "number"
  ) {
    const positives = vtDomainStats.positives;
    const total = vtDomainStats.total || 0;
    vtDomainScore = total ? `${positives}/${total}` : `${positives}/?`;
    vtDomainScoreClass =
      positives > 0
        ? "summary-entity-vt summary-entity-vt-bad"
        : "summary-entity-vt summary-entity-vt-good";
  }

  const senderValidation = result?.senderValidation || {};
  const spfResult = senderValidation?.spf?.result || "unknown";
  const dkimResult = senderValidation?.dkim?.result || "unknown";
  const dmarcResult = senderValidation?.dmarc?.result || "unknown";
  const spfDetail = senderValidation?.spf?.detail;
  const dkimDetail = senderValidation?.dkim?.detail;
  const dmarcDetail = senderValidation?.dmarc?.detail;

  const getDomainFromAddr = (addr = "") => {
    const m = String(addr).match(/@([^>\s]+)/);
    return m ? m[1].toLowerCase() : "";
  };

  const fromAddress = result?.headers?.from || "Unknown sender";
  const toAddress = result?.headers?.to || "Unknown recipient";

  const fromDomainText = headerSec?.fromDomain || getDomainFromAddr(fromAddress) || "unknown";
  const toDomainText = getDomainFromAddr(toAddress);

  const senderType =
    fromDomainText && toDomainText && fromDomainText === toDomainText ? "internal" : "external";

  const urlsArr = result?.urls || [];

  let sourceIp = null;
  if (receivedFlow.length > 0 && receivedFlow[0].line) {
    const ipMatch = receivedFlow[0].line.match(/\b\d{1,3}(?:\.\d{1,3}){3}\b/);
    sourceIp = ipMatch ? ipMatch[0] : null;
  }

  const vtDomainUrl =
    fromDomainText && fromDomainText !== "unknown"
      ? `https://www.virustotal.com/gui/domain/${encodeURIComponent(fromDomainText)}/details`
      : null;

  const vtIpUrl = sourceIp
    ? `https://www.virustotal.com/gui/ip-address/${encodeURIComponent(sourceIp)}/details`
    : null;

  const attachmentCount = (result?.attachments || []).length;
  const primaryAttachment = attachmentCount === 1 ? result.attachments[0] : null;

  const formatControlSummary = (ctrl, label) => {
    if (!ctrl || !ctrl.result) return `${label}: not evaluated`;
    const res = String(ctrl.result).toLowerCase();
    const pass = res === "pass";
    const aligned = ctrl.alignedWithFrom;
    const compliant = pass && aligned ? "Compliant" : "Non-compliant";
    return `${label}: ${res.toUpperCase()} (${compliant})`;
  };

  const spfSummary = formatControlSummary(senderValidation.spf, "SPF");
  const dkimSummary = formatControlSummary(senderValidation.dkim, "DKIM");
  const dmarcSummary = formatControlSummary(senderValidation.dmarc, "DMARC");

  const primaryUrl = urlsArr[0] || null;

  const vtLabel = primaryUrl?.vt ? primaryUrl.vt.verdict || "unknown" : "not checked";
  const abuseLabel = primaryUrl?.abuseIpdb
    ? typeof primaryUrl.abuseIpdb.score === "number"
      ? `${primaryUrl.abuseIpdb.score}/100`
      : primaryUrl.abuseIpdb.classification || "unknown"
    : "not checked";
  const ipqsLabel = primaryUrl?.ipqs
    ? typeof primaryUrl.ipqs.score === "number"
      ? `${primaryUrl.ipqs.score}/100`
      : primaryUrl.ipqs.risk || "unknown"
    : "not checked";
  const urlscanLabel = primaryUrl?.urlscan ? primaryUrl.urlscan.verdict || "unknown" : "not checked";
  const otxLabel = primaryUrl?.otx ? primaryUrl.otx.verdict || "unknown" : "not checked";

  const detoMalicious = adv.detoMalicious;
  const detoSuspicious = adv.detoSuspicious;
  const detoComplete = adv.detoComplete;
  const detoVerdict = adv.detoVerdict;

  // Per-engine verdicts for the Overall Outcome breakdown. The server reconciles
  // AI + Detection Engine into result.finalVerdict via the verdict matrix
  // (Clean < Needs Review < Spam < Malicious); we trust that result and then
  // fold in the sandbox detonation as a third engine (escalate-only).
  const heuristicVerdict = result?.heuristicVerdict || result?.verdict || "Unknown";
  const heuristicReason = result?.heuristicReason || "";
  const aiRawVerdict = aiEnabledForResult ? result?.aiRawVerdict || "" : "";
  const aiReason = aiEnabledForResult ? result?.aiReason || "" : "";
  const determinationReason = aiEnabledForResult ? result?.determinationReason || "" : "";

  // Surface AI failures explicitly so a provider/model misconfiguration is
  // obvious and not mistaken for a detection-engine regression.
  const aiRequested = result?.aiRequested ?? aiEnabledForResult;
  const aiError = aiRequested ? result?.aiError || "" : "";
  const aiProviderLabel = [result?.aiProvider, result?.aiModel].filter(Boolean).join(" / ");

  const reconciledVerdict =
    (aiEnabledForResult && (result?.finalVerdict || result?.aiVerdict)) ||
    result?.verdict ||
    "Unknown";

  // Fold the sandbox detonation in as a third engine (escalate-only):
  // malicious → Malicious (hard-confirmed), suspicious → Needs Review.
  const {
    verdict: finalVerdict,
    raisedBySandbox,
  } = foldSandboxVerdict(reconciledVerdict, { detoMalicious, detoSuspicious });
  const finalVerdictClass = mapVerdictToClass(finalVerdict);

  // A Malicious verdict based on deceptive intent (not a confirmed technical
  // indicator) is flagged for analyst re-review. A confirmed-malicious sandbox
  // detonation IS hard proof, so it clears the flag.
  const analystReviewAdvised = detoMalicious ? false : !!result?.analystReviewAdvised;
  const analystReviewReason = result?.analystReviewReason || "";

  // Allowlisted IoCs are EXCLUDED from the verdict, but never relabel it: the
  // banner always shows the real computed verdict (Malicious / Clean / …). When
  // an allowlisted IoC was independently flagged malicious/suspicious we surface
  // it as a clear note, and — when it was the *only* malicious factor (so the
  // verdict reads benign) — keep a RED CAUTION BORDER so the hidden threat is
  // visible at a glance.
  const whitelistedMaliciousIndicators =
    (Array.isArray(result?.whitelistedMaliciousIndicators) && result.whitelistedMaliciousIndicators) ||
    (Array.isArray(result?.whitelistOverrideReasons) ? result.whitelistOverrideReasons : []);
  const hasWhitelistedMalicious = whitelistedMaliciousIndicators.length > 0;
  // The allowlist actually lowered the verdict (an allowlisted IoC was the sole threat).
  const whitelistSuppressed = !!result?.whitelistOverride;
  const whitelistMasksMalicious = hasWhitelistedMalicious && finalVerdictClass !== "pill-danger";
  const bannerVerdict = finalVerdict;
  const bannerVerdictClass = finalVerdictClass;
  const whitelistNoteHead = whitelistSuppressed
    ? `Verdict remains ${formatVerdict(finalVerdict)} — the malicious indicator(s) below were allowlisted by an administrator and excluded from scoring:`
    : "Allowlisted indicators flagged malicious by reputation, excluded from the verdict:";

  const verdictIcon =
    bannerVerdictClass === "pill-danger" ? (
      <FaExclamationTriangle />
    ) : bannerVerdictClass === "pill-ok" ? (
      <FaCheckCircle />
    ) : bannerVerdictClass === "pill-warn" ? (
      <FaExclamationCircle />
    ) : (
      <FaShieldAlt />
    );

  const handleExportPdf = async () => {
    setOptionsOpen(false);
    if (!result) return;
    try {
      const resp = await fetch(exportEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report: result }),
      });
      if (!resp.ok) throw new Error("Failed to generate PDF");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `phishing-report-${result.fileName || "email"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[phishing] PDF export error:", e);
      setActionError("PDF export failed. Please try again.");
      setTimeout(() => setActionError(""), 4000);
    }
  };

  const handleSendServiceNow = () => {
    setOptionsOpen(false);
    console.log("[phishing] Send to ServiceNow clicked");
  };

  const handleAiCorrelation = () => {
    setOptionsOpen(false);
    console.log("[phishing] AI correlation clicked");
  };

  /* ============================================================
     RENDER
     ============================================================ */
  return (
    <>
      <div className="results-wrapper">
        {actionError && <div className="error-banner">{actionError}</div>}

        <div className="card overall-outcome-card">
          <div className="card-header overall-header">
            <h3>Overall Analysis Outcome</h3>

            <div className="card-options">
              <button
                type="button"
                className="clear-verdict-btn"
                onClick={() => {
                  setOptionsOpen(false);
                  setShowRawHeader(false);
                  if (onClear) onClear();
                }}
                title="Clear verdict"
                aria-label="Clear verdict"
              >
                <FaTimes />
              </button>

              <button
                type="button"
                className="card-options-btn"
                onClick={() => setOptionsOpen((open) => !open)}
              >
                <span className="sr-only">More options</span>
                <FaEllipsisV />
              </button>

              {optionsOpen && (
                <div className="card-options-menu">
                  <button type="button" className="card-options-item" onClick={handleExportPdf}>
                    Export as PDF
                  </button>
                  <button type="button" className="card-options-item" onClick={handleSendServiceNow}>
                    Send to ServiceNow
                  </button>
                  <button type="button" className="card-options-item" onClick={handleAiCorrelation}>
                    AI correlation
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── VERDICT BANNER ── */}
          <div
            className={`verdict-banner vb-${bannerVerdictClass.replace("pill-", "")} ${
              hasWhitelistedMalicious ? "vb-whitelisted" : ""
            } ${whitelistMasksMalicious ? "vb-whitelist-danger" : ""}`}
          >
            <div className="vb-glow" />
            <div className="vb-icon">{verdictIcon}</div>
            <div className="vb-content">
              <div className="vb-label">
                Verdict
                {hasWhitelistedMalicious && (
                  <span
                    className={`vb-override-tag ${
                      whitelistMasksMalicious ? "vb-override-tag-danger" : ""
                    }`}
                  >
                    {whitelistSuppressed
                      ? `ALLOWLISTED IoC EXCLUDED — would otherwise be ${formatVerdict(result.originalVerdict)}`
                      : "ALLOWLISTED IoC EXCLUDED FROM VERDICT"}
                  </span>
                )}
              </div>
              <div className="vb-value">{formatVerdict(bannerVerdict)}</div>
              {raisedBySandbox && (
                <div className="vb-override-tag">
                  {detoMalicious
                    ? "ESCALATED — a sandbox detonation confirmed malicious behavior"
                    : `ESCALATED — a sandbox detonation flagged suspicious behavior; routed to ${formatVerdict(finalVerdict)}`}
                </div>
              )}

              {/* AI failed (or was unavailable) — say so loudly instead of silently degrading */}
              {aiError && (
                <div className="vb-ai-error" role="alert">
                  <span className="vb-ai-error-icon" aria-hidden="true">⚠</span>
                  <span className="vb-ai-error-text">
                    <strong>
                      AI analysis unavailable
                      {aiProviderLabel ? ` (${aiProviderLabel})` : ""}:
                    </strong>{" "}
                    {aiError} Showing the Detection Engine result only.
                  </span>
                </div>
              )}

              {/* Per-engine breakdown: AI vs Detection Engine, each with a one-line reason */}
              {aiEnabledForResult && aiRawVerdict && (
                <div className="vb-engines">
                  <div className="vb-engine-line">
                    <span className="vb-engine-tag">AI</span>
                    <span className={`pill ${mapVerdictToClass(aiRawVerdict)}`}>
                      {formatVerdict(aiRawVerdict)}
                    </span>
                    {aiReason && <span className="vb-engine-reason">{aiReason}</span>}
                  </div>
                  <div className="vb-engine-line">
                    <span className="vb-engine-tag">Detection Engine</span>
                    <span className={`pill ${mapVerdictToClass(heuristicVerdict)}`}>
                      {formatVerdict(heuristicVerdict)}
                    </span>
                    {heuristicReason && <span className="vb-engine-reason">{heuristicReason}</span>}
                  </div>
                </div>
              )}
              {/* Sandbox detonation as a third engine */}
              {detoComplete && detoVerdict &&
                !["pending", "unknown"].includes(String(detoVerdict).toLowerCase()) && (
                  <div className="vb-engines">
                    <div className="vb-engine-line">
                      <span className="vb-engine-tag">Sandbox</span>
                      <span className={`pill ${mapVerdictToClass(detoVerdict)}`}>
                        {formatVerdict(detoVerdict)}
                      </span>
                      <span className="vb-engine-reason">
                        {detoMalicious
                          ? "Detonation confirmed malicious behavior."
                          : detoSuspicious
                          ? "Detonation observed suspicious behavior."
                          : "Detonation observed no malicious behavior."}
                      </span>
                    </div>
                  </div>
                )}
              {aiEnabledForResult && determinationReason && (
                <div className="vb-determination">{determinationReason}</div>
              )}
              {raisedBySandbox && (
                <div className="vb-determination">
                  Advanced sandbox detonation returned “{formatVerdict(detoVerdict)}” — overall verdict raised to {formatVerdict(finalVerdict)}.
                </div>
              )}
              {analystReviewAdvised && (
                <div className="vb-analyst-review" role="note">
                  <strong>⚑ Analyst review advised</strong>
                  {analystReviewReason ? ` — ${analystReviewReason}` : ""}
                </div>
              )}

              <div className="vb-sub">
                {result.fileName} · {senderType} sender · {fromDomainText}
              </div>
              {hasWhitelistedMalicious && (
                <div className="vb-whitelist-note">
                  <div className="vb-determination">{whitelistNoteHead}</div>
                  <ul className="vb-override-reasons">
                    {whitelistedMaliciousIndicators.map((r, i) => (
                      <li key={i}>{r.note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <HomoglyphFindings homoglyphs={result.homoglyphs} />
          <CustomRuleFindings customRules={result.customRules} />

          {/* ── SIGNAL SCORECARD ── */}
          <div className="signal-row">
            {[
              { label: "Header Risk", value: headerSec?.riskLevel?.toUpperCase() || "?", cls: headerRiskClass },
              { label: "SPF", value: spfResult.toUpperCase(), cls: spfResult === "pass" ? "pill-ok" : "pill-danger" },
              { label: "DKIM", value: dkimResult.toUpperCase(), cls: dkimResult === "pass" ? "pill-ok" : "pill-danger" },
              { label: "DMARC", value: dmarcResult.toUpperCase(), cls: dmarcResult === "pass" ? "pill-ok" : "pill-danger" },
              { label: "Attachments", value: String(attachmentCount), cls: attachmentCount > 0 ? "pill-warn" : "pill-ok" },
              { label: "URLs", value: String(urlsArr.length), cls: urlsArr.length > 0 ? "pill-warn" : "pill-ok" },
              { label: "QR Codes", value: String(qrCount), cls: qrCount > 0 ? "pill-warn" : "pill-ok" },
            ].map(({ label, value, cls }) => (
              <div key={label} className={`signal-chip sc-${cls.replace("pill-", "")}`}>
                <div className="sc-value">{value}</div>
                <div className="sc-label">{label}</div>
              </div>
            ))}
          </div>

          {adv.showAdvanced && (
            <div style={{ display: "flex", justifyContent: "flex-end", margin: "14px 0" }}>
              <AdvancedAnalysisButton adv={adv} onStart={() => setActiveTab("advanced")} />
            </div>
          )}

          <div
            className={`overall-ai-block ${
              aiEnabledForResult ? `ai-verdict-${verdictClass.replace("pill-", "")}` : "ai-verdict-neutral"
            }`}
          >
            <div className="sub-header-row">
              <h4>AI Analysis Outcome</h4>
              <span className={`pill ${aiEnabledForResult ? verdictClass : "pill-neutral"}`}>
                {aiEnabledForResult ? formatVerdict(verdictText) : "AI Disabled"}
              </span>
            </div>

            <p className="ai-text">
              {aiEnabledForResult
                ? renderAiOutcome(result.aiAnalysis) || "AI was enabled but no AI summary was returned."
                : "AI analysis was disabled for this run. Static header and threat intelligence checks were executed normally."}
            </p>
          </div>

          <div className="overall-summary-block">
            <h4>Analysis Summary</h4>

            <div className="analysis-summary-text">
              <p className="analysis-summary-intro">
                This email was received from an <strong>{senderType}</strong> sender{" "}
                <strong>{fromAddress}</strong> using the domain{" "}
                <strong className="summary-entity">
                  {fromDomainText}
                  {vtDomainScore && (
                    <span className={vtDomainScoreClass} title="VirusTotal detections / total engines">
                      VirusTotal : {vtDomainScore}
                    </span>
                  )}
                  {fromDomainText && fromDomainText !== "unknown" && (
                    <IocInfoPopover type="domain" value={fromDomainText} />
                  )}
                  {vtDomainUrl && (
                    <a
                      href={vtDomainUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="summary-entity-link"
                      title="Open domain in VirusTotal"
                    >
                      <FaExternalLinkAlt />
                    </a>
                  )}
                </strong>
                {sourceIp && (
                  <>
                    {" "}
                    and originating from IP{" "}
                    <strong className="summary-entity">
                      {sourceIp}
                      <IocInfoPopover type="ip" value={sourceIp} />
                      {vtIpUrl && (
                        <a
                          href={vtIpUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="summary-entity-link"
                          title="Open IP in VirusTotal"
                        >
                          <FaExternalLinkAlt />
                        </a>
                      )}
                    </strong>
                  </>
                )}
                . The evaluated header risk is <strong>{headerSec?.riskLevel || "unknown"}</strong>.
              </p>

              <ul className="analysis-summary-list">
                <li>
                  <strong>Sender controls</strong> – {spfSummary}; {dkimSummary}; {dmarcSummary}.
                </li>

                <li>
                  <strong>Attachments</strong> –{" "}
                  {attachmentCount === 0 && "No attachments present."}
                  {attachmentCount === 1 && primaryAttachment && (
                    <>
                      1 attachment (<span>{primaryAttachment.name}</span>,{" "}
                      <span>{primaryAttachment.mime}</span>, <span>{primaryAttachment.sizeKB} KB</span>) with SHA1{" "}
                      <span className="mono">{primaryAttachment.sha1}</span> and SHA256{" "}
                      <span className="mono">{primaryAttachment.sha256}</span>.
                    </>
                  )}
                  {attachmentCount > 1 &&
                    `${attachmentCount} attachments present; all hashed with SHA1 and SHA256.`}
                </li>

                <li>
                  <strong>OSINT validations</strong> –{" "}
                  {urlsArr.length === 0 ? (
                    "no clickable URLs extracted."
                  ) : (
                    <>
                      {urlsArr.length} URL{urlsArr.length > 1 ? "s" : ""} analysed. For the primary URL: VT{" "}
                      {vtLabel}, AbuseIPDB {abuseLabel}, IPQS {ipqsLabel}, URLScan.io {urlscanLabel}, AlienVault
                      OTX {otxLabel}.
                    </>
                  )}
                </li>

                <li>
                  <strong>Email body analysis</strong> –{" "}
                  {!socialIntent
                    ? "not available."
                    : `level ${String(socialLevel).toUpperCase()} (score ${socialScore}); ${socialSummaryText}`}
                </li>

                <li>
                  <strong>QR intelligence</strong> –{" "}
                  {qrCount === 0
                    ? "no QR codes detected in body or attachments."
                    : `${qrCount} QR code${qrCount > 1 ? "s" : ""} decoded (example: ${qrPreview}).`}
                </li>

                <li>
                  <strong>Homoglyph pattern</strong> –{" "}
                  {!result?.homoglyphs ? (
                    <em>not evaluated for this analysis (re-run after adding protected domains).</em>
                  ) : result.homoglyphs.matchedCount > 0 ? (
                    <>
                      <strong className="summary-flag-yes">Yes</strong> — {result.homoglyphs.findings[0].domain}{" "}
                      resembles protected domain {result.homoglyphs.findings[0].matched}
                      {result.homoglyphs.matchedCount > 1
                        ? ` (and ${result.homoglyphs.matchedCount - 1} more)`
                        : ""}
                      .
                    </>
                  ) : (
                    <>
                      <strong className="summary-flag-no">No</strong> — no sender, reply-to, or link domain
                      resembles a protected domain.
                    </>
                  )}
                </li>

                <li>
                  <strong>Final verdict</strong> – classified as <strong>{formatVerdict(finalVerdict)}</strong>{" "}
                  based on the combination of header risk, sender authentication results, attachment profile and
                  OSINT outcomes.
                </li>
              </ul>
            </div>
          </div>

          <div className="overall-summary-block">
            <h4>Spoofing & Header Anomalies</h4>

            {headerSec.anomalies?.length > 0 ? (
              <div className="anomaly-cards">
                {headerSec.anomalies.map((a, i) => (
                  <div
                    key={i}
                    className={`anomaly-card ${
                      a.severity === "high" ? "anomaly-high" : a.severity === "medium" ? "anomaly-med" : "anomaly-low"
                    }`}
                  >
                    <div className="anomaly-card-top">
                      <span className="anomaly-type">{a.type}</span>
                      <span
                        className={`pill pill-sm ${
                          a.severity === "high" ? "pill-danger" : a.severity === "medium" ? "pill-warn" : "pill-info"
                        }`}
                      >
                        {a.severity}
                      </span>
                    </div>
                    <p className="anomaly-desc">{a.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="placeholder">No header anomalies detected.</p>
            )}
          </div>
        </div>

        <div className="phish-tabs">
          {[
            { id: "header", icon: <FaList />, label: "Header Analysis" },
            { id: "controls", icon: <FaUserShield />, label: "Sender Controls" },
            { id: "attachments", icon: <FaPaperclip />, label: "Attachments" },
            { id: "body", icon: <FaFileAlt />, label: "Email Body" },
            { id: "analysis", icon: <FaUserSecret />, label: "Social Engineering" },
            { id: "qr", icon: <FaQrcode />, label: "QR Intelligence" },
            { id: "urls", icon: <FaLink />, label: "URLs & Threat Intel" },
            ...(adv.showAdvanced ? [{ id: "advanced", icon: <FaShieldAlt />, label: "Advanced Analysis" }] : []),
          ].map(({ id, icon, label }) => (
            <button
              key={id}
              type="button"
              className={`phish-tab-btn ${activeTab === id ? "active" : ""}`}
              onClick={() => setActiveTab(id)}
            >
              <span className="tab-icon">{icon}</span>
              <span className="tab-label">{label}</span>
            </button>
          ))}
        </div>

        {activeTab === "header" && (
          <div className="results-grid">
            <div className="column">
              <div className="card">
                <div className="card-header">
                  <h3>Header Analysis</h3>
                </div>

                <div className="kv">
                  <div>
                    <span>From</span>
                    <code>{result.headers?.from}</code>
                  </div>
                  <div>
                    <span>To</span>
                    <code>{result.headers?.to}</code>
                  </div>
                  <div>
                    <span>Subject</span>
                    <code>{result.headers?.subject}</code>
                  </div>
                  <div>
                    <span>Reply-To</span>
                    <code>{result.headers?.replyTo || "—"}</code>
                  </div>
                  <div>
                    <span>Return-Path</span>
                    <code>{result.headers?.returnPath || "—"}</code>
                  </div>

                  {replyCheck && (
                    <div className="replyto-status">
                      <span className="replyto-label">From vs Reply-To</span>
                      <span className={`pill ${replyRiskClass}`}>
                        {replyCheck.status === "match"
                          ? "Addresses match"
                          : replyCheck.status === "domain-match-local-mismatch"
                          ? "Caution: mailbox differs"
                          : replyCheck.status === "domain-mismatch"
                          ? "Suspicious: domain mismatch"
                          : "No Reply-To header"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="subpanel">
                  <div className="subpanel-title">
                    <span>Received Path</span>
                    <span className="subpanel-hint">*Origin at the top, delivery at the bottom</span>
                  </div>

                  {receivedFlow.length > 0 ? (
                    <div className="received-flow">
                      <div
                        className={`hop-envelope hop-envelope-${receivedFlow[0]?.risk || "ok"}`}
                        aria-hidden="true"
                      >
                        <FaEnvelope />
                      </div>
                      {receivedFlow.map((hop, i) => (
                        <React.Fragment key={i}>
                          <div className={`hop hop-risk-${hop.risk}`}>
                            <div className="hop-header">
                              <span className="hop-role">{hop.role}</span>
                              <span
                                className={`pill hop-pill ${hopPillClass(hop.risk)}`}
                              >
                                {hop.riskLabel}
                              </span>
                            </div>
                            <pre className="hop-line mono">{hop.line}</pre>
                          </div>
                          {i < receivedFlow.length - 1 && (
                            <div className="hop-arrow">
                              <FaArrowDown />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  ) : (
                    <p className="placeholder">No Received headers found.</p>
                  )}
                </div>

                <div className="subpanel raw-header-subpanel">
                  <div className="subpanel-title">
                    <span>Raw Headers</span>
                    <div className="raw-header-actions">
                      {copyStatus && (
                        <span
                          className={`copy-status ${
                            copyStatus === "copied" ? "copy-status-ok" : "copy-status-bad"
                          }`}
                        >
                          {copyStatus === "copied" ? "Copied" : "Copy failed"}
                        </span>
                      )}
                      <button type="button" className="link-btn" onClick={handleCopyHeaders}>
                        <FaCopy />
                      </button>
                      <button type="button" className="link-btn" onClick={() => setShowRawHeader((v) => !v)}>
                        {showRawHeader ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  {showRawHeader && (
                    <pre className="raw-headers mono">
                      {result.headers?.raw || "Raw header not available."}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "controls" && (
          <div className="results-grid">
            <div className="column">
              <div className="card">
                <div className="card-header">
                  <h3>Sender Controls</h3>
                </div>

                <div className="auth-cards-grid">
                  {[
                    { name: "SPF", res: spfResult, detail: spfDetail },
                    { name: "DKIM", res: dkimResult, detail: dkimDetail },
                    { name: "DMARC", res: dmarcResult, detail: dmarcDetail },
                  ].map(({ name, res, detail }) => (
                    <div key={name} className={`auth-card ${res === "pass" ? "auth-card-ok" : "auth-card-fail"}`}>
                      <div className="auth-card-icon">
                        {res === "pass" ? <FaCheckCircle /> : <FaExclamationTriangle />}
                      </div>
                      <div className="auth-card-name">{name}</div>
                      <div className={`pill auth-card-badge ${res === "pass" ? "pill-ok" : "pill-danger"}`}>
                        {res.toUpperCase()}
                      </div>
                      <p className="auth-card-detail">{detail || `No ${name} info available.`}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "attachments" && (
          <div className="results-grid">
            <div className="column">
              <div className="card">
                <div className="card-header">
                  <h3>Attachments</h3>
                </div>

                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>File</th>
                        <th>Type</th>
                        <th>Size (KB)</th>
                        <th>SHA1</th>
                        <th>SHA256</th>
                        <th>VirusTotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.attachments || []).length === 0 && (
                        <tr>
                          <td colSpan="6" className="placeholder-row">
                            No attachments detected.
                          </td>
                        </tr>
                      )}

                      {(result.attachments || []).map((a, i) => {
                        const vt = a.vt || null;
                        const vtVerdict = vt?.verdict || "unknown";

                        let vtCellLabel = "N/A";
                        if (vt) {
                          if (vtVerdict === "malicious" || vtVerdict === "suspicious") {
                            const pos = vt.positives ?? "?";
                            const tot = vt.total ?? "?";
                            vtCellLabel = `${formatVerdict(vtVerdict)} (${pos}/${tot})`;
                          } else if (vtVerdict === "clean") {
                            vtCellLabel = "clean";
                          } else if (vtVerdict === "not-configured") {
                            vtCellLabel = "not configured";
                          } else {
                            vtCellLabel = formatVerdict(vtVerdict);
                          }
                        }

                        return (
                          <tr key={i}>
                            <td>{a.name}</td>
                            <td className="mono">{a.mime}</td>
                            <td>{a.sizeKB}</td>
                            <td className="mono mono-wrap hash">{a.sha1}</td>
                            <td className="mono mono-wrap hash">{a.sha256}</td>
                            <td>
                              {vt ? (
                                vt.rateLimited ? (
                                  <RateTag />
                                ) : (
                                <a
                                  href={vt.reportLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`vt-pill vt-pill-${vtVerdict}`}
                                  title="View VirusTotal report"
                                >
                                  {vtCellLabel}
                                </a>
                                )
                              ) : (
                                <span className="muted">N/A</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "body" && (
          <div className="results-grid">
            <div className="column">
              <div className="card email-body-card">
                <div className="card-header">
                  <h3>Email Body</h3>
                </div>

                {result.emailBodyText ? (
                  <div className="email-body-wrapper">
                    <pre className="email-body-text">{result.emailBodyText}</pre>
                  </div>
                ) : (
                  <p className="placeholder">Email body not available or empty.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "analysis" && (
          <div className="results-grid">
            <div className="column">
              <div className="card">
                <div className="card-header">
                  <h3>Email Body Analysis</h3>
                </div>

                {!socialIntent ? (
                  <p className="placeholder">No social engineering analysis available.</p>
                ) : (
                  <>
                    <div className="social-header">
                      <div className="social-level-wrap">
                        <div className="social-level-label">Risk Level</div>
                        <span className={`pill social-level-badge ${socialPillClass}`}>
                          {String(socialLevel).toUpperCase()}
                        </span>
                      </div>
                      <div className="social-score-wrap">
                        <div className="social-score-label">Indicator Score</div>
                        <div className="social-score-meter">
                          <div className="social-score-track">
                            <div
                              className={`social-score-fill ${socialPillClass}`}
                              style={{ width: `${Math.min(100, socialScore * 10)}%` }}
                            />
                          </div>
                          <span className="social-score-num">{socialScore}</span>
                        </div>
                      </div>
                    </div>

                    <p className="ai-text social-summary">{socialSummaryText}</p>

                    {(() => {
                      const entries = Object.entries(socialIndicators || {})
                        .map(([k, hits]) => ({ key: k, hits: Array.isArray(hits) ? hits : [] }))
                        .filter((e) => e.hits.length > 0);

                      if (entries.length === 0) {
                        return (
                          <p className="placeholder" style={{ marginTop: 10 }}>
                            No matched social engineering keywords were found.
                          </p>
                        );
                      }

                      return (
                        <div className="social-categories">
                          <p className="social-cat-hint">*Keyword-based matches — review in context</p>
                          {entries.map((e) => (
                            <div key={e.key} className="social-cat-card">
                              <div className="social-cat-name">{humanizeCat(e.key)}</div>
                              <div className="social-cat-hits">
                                {e.hits.map((hit, i) => (
                                  <span key={i} className="social-hit-tag">
                                    {hit}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "qr" && (
          <div className="results-grid">
            <div className="column">
              <div className="card">
                <div className="card-header">
                  <h3>QR Intelligence</h3>
                </div>

                {(() => {
                  const all = allQrForSummary;

                  if (all.length === 0) {
                    return <p className="placeholder">No QR codes detected in body or attachments.</p>;
                  }

                  return (
                    <div className="table-wrapper">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Source</th>
                            <th>Image</th>
                            <th>Type</th>
                            <th>Decoded</th>
                          </tr>
                        </thead>
                        <tbody>
                          {all.map((x, i) => {
                            const parsed = parseQrPayload(x.qrText);
                            return (
                              <tr key={i}>
                                <td>{x.source}</td>
                                <td className="mono mono-wrap">{x.name}</td>
                                <td>
                                  <span className="pill pill-info">{parsed.type}</span>
                                </td>
                                <td>{renderQrValue(parsed)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === "urls" && (
          <div className="results-grid">
            <div className="column">
              <div className="card urls-card-wide">
                <div className="card-header urls-card-header">
                  <h3>URLs & Threat Intel</h3>
                  {result.urls?.length > 0 && (
                    <span className="urls-count-pill">
                      {result.urls.length} URL{result.urls.length > 1 ? "s" : ""} analysed
                    </span>
                  )}
                </div>

                <div className="table-wrapper urls-table-wrapper">
                  <table className="table url-intel-table">
                    <thead>
                      <tr>
                        <th>URL</th>
                        <th>Overall</th>
                        <th>VirusTotal</th>
                        <th>AbuseIPDB</th>
                        <th>IPQualityScore</th>
                        <th>URLScan.io</th>
                        <th>AlienVault OTX</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.urls || []).length === 0 && (
                        <tr>
                          <td colSpan="7" className="placeholder-row">
                            No URLs extracted.
                          </td>
                        </tr>
                      )}

                      {(result.urls || []).map((u, i) => {
                        const overallVerdict = u.overallVerdict || u.vt?.verdict || "unknown";

                        const abuseScore = u.abuseIpdb?.score;
                        const abuseLabel2 =
                          u.abuseIpdb?.classification ||
                          (typeof abuseScore === "number" ? `${abuseScore}/100` : "unknown");

                        const ipqsLabel2 =
                          typeof u.ipqs?.score === "number" ? `${u.ipqs.score}/100` : u.ipqs?.risk || "unknown";

                        const urlscanVerdict = u.urlscan?.verdict || "unknown";
                        const otxVerdict = u.otx?.verdict || "unknown";

                        return (
                          <tr key={i} className={u.whitelisted ? "url-row-whitelisted" : ""}>
                            <td className="mono mono-wrap url-cell">
                              <span className="url-main">{u.url}</span>
                              {u.whitelisted && (
                                <span className="wl-badge" title={u.whitelistNote || "Allowlisted by an administrator"}>
                                  <FaShieldAlt /> Whitelisted
                                </span>
                              )}
                            </td>

                            <td>
                              <span className={`pill ${mapVerdictToClass(overallVerdict)}`}>
                                {u.whitelisted ? "Whitelisted" : mapVerdictToLabel(overallVerdict)}
                              </span>
                            </td>

                            <td>
                              {u.vt ? (
                                u.vt.rateLimited ? (
                                  <RateTag />
                                ) : (
                                <div className="intel-cell">
                                  <span className={`pill ${mapVerdictToClass(u.vt.verdict)}`}>
                                    {mapVerdictToLabel(u.vt.verdict)}
                                  </span>
                                  <div className="intel-meta">
                                    {typeof u.vt.positives === "number" && typeof u.vt.total === "number"
                                      ? `${u.vt.positives}/${u.vt.total} detections`
                                      : "detections not available"}
                                  </div>
                                  {u.vt.last_scan && <div className="intel-meta">Last scan: {u.vt.last_scan}</div>}
                                  {u.vt.reportLink && (
                                    <div className="intel-meta">
                                      <a href={u.vt.reportLink} target="_blank" rel="noreferrer" className="intel-link">
                                        View report
                                      </a>
                                    </div>
                                  )}
                                </div>
                                )
                              ) : (
                                <span className="intel-na">N/A</span>
                              )}
                            </td>

                            <td>
                              {u.abuseIpdb ? (
                                u.abuseIpdb.rateLimited ? (
                                  <RateTag />
                                ) : (
                                <div className="intel-cell">
                                  <span className={`pill ${mapVerdictToClass(u.abuseIpdb.classification || abuseScore)}`}>
                                    {abuseLabel2}
                                  </span>

                                  {typeof abuseScore === "number" && (
                                    <div className="intel-meta">Score: {abuseScore}/100</div>
                                  )}
                                  {u.abuseIpdb.totalReports !== undefined && (
                                    <div className="intel-meta">Reports: {u.abuseIpdb.totalReports}</div>
                                  )}
                                  {u.abuseIpdb.lastReportedAt && (
                                    <div className="intel-meta">Last report: {u.abuseIpdb.lastReportedAt}</div>
                                  )}
                                  {u.abuseIpdb.reportLink && (
                                    <div className="intel-meta">
                                      <a href={u.abuseIpdb.reportLink} target="_blank" rel="noreferrer" className="intel-link">
                                        View report
                                      </a>
                                    </div>
                                  )}
                                </div>
                                )
                              ) : (
                                <span className="intel-na">N/A</span>
                              )}
                            </td>

                            <td>
                              {u.ipqs ? (
                                u.ipqs.rateLimited ? (
                                  <RateTag />
                                ) : (
                                <div className="intel-cell">
                                  <span className={`pill ${mapVerdictToClass(u.ipqs.risk || u.ipqs.score)}`}>
                                    {mapVerdictToLabel(u.ipqs.risk || "unknown")}
                                  </span>

                                  {typeof u.ipqs.score === "number" && (
                                    <div className="intel-meta">Score: {ipqsLabel2}</div>
                                  )}

                                  <div className="intel-meta">
                                    {u.ipqs.isProxy && "Proxy "}
                                    {u.ipqs.isVpn && "VPN "}
                                    {u.ipqs.isTor && "Tor "}
                                    {!u.ipqs.isProxy && !u.ipqs.isVpn && !u.ipqs.isTor && "No proxy/VPN flags"}
                                  </div>
                                </div>
                                )
                              ) : (
                                <span className="intel-na">N/A</span>
                              )}
                            </td>

                            <td>
                              {u.urlscan ? (
                                u.urlscan.rateLimited ? (
                                  <RateTag />
                                ) : (
                                <div className="intel-cell">
                                  <span className={`pill ${mapVerdictToClass(urlscanVerdict)}`}>
                                    {mapVerdictToLabel(urlscanVerdict)}
                                  </span>
                                  {u.urlscan.scanDate && (
                                    <div className="intel-meta">Last scan: {u.urlscan.scanDate}</div>
                                  )}
                                  {u.urlscan.reportLink && (
                                    <div className="intel-meta">
                                      <a href={u.urlscan.reportLink} target="_blank" rel="noreferrer" className="intel-link">
                                        View report
                                      </a>
                                    </div>
                                  )}
                                </div>
                                )
                              ) : (
                                <span className="intel-na">N/A</span>
                              )}
                            </td>

                            <td>
                              {u.otx ? (
                                u.otx.rateLimited ? (
                                  <RateTag />
                                ) : (
                                <div className="intel-cell">
                                  <span className={`pill ${mapVerdictToClass(otxVerdict)}`}>
                                    {mapVerdictToLabel(otxVerdict)}
                                  </span>
                                  {u.otx.pulseCount !== undefined && (
                                    <div className="intel-meta">Pulses: {u.otx.pulseCount}</div>
                                  )}
                                  {u.otx.reportLink && (
                                    <div className="intel-meta">
                                      <a href={u.otx.reportLink} target="_blank" rel="noreferrer" className="intel-link">
                                        View report
                                      </a>
                                    </div>
                                  )}
                                </div>
                                )
                              ) : (
                                <span className="intel-na">N/A</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "advanced" && <AdvancedAnalysisCard adv={adv} />}
      </div>

      <AdvancedAnalysisModal adv={adv} />

      <PhishyAssistant result={result} aiEnabled={aiEnabled} />
    </>
  );
}
